import { Component, ViewChild, ViewChildren, ElementRef, QueryList, ChangeDetectorRef, effect, signal } from '@angular/core';
import { Router, ActivatedRoute, RouterLink, Scroll } from '@angular/router';

import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from '@environment';

import { MenuService } from '@core/services/menu/menu.service';
import { CartService } from '@core/services/cart/cart.service';
import { OutletService } from '@core/services/outlet/outlet.service';
import { FilterService } from '@core/services/filter/filter.service';
import { PriceService } from '@core/services/price/price.service';
import { PassService } from '@core/services/pass/pass.service';
import { OutletStatusService } from '@core/services/outlet/outlet-status.service';
import { StorageService } from '@core/services/util/storage.service';
import { MenuFilterService } from '@core/services/menu_filter/menu-filter.service';
import { MenuUtilService } from '@core/services/util/menu-util.service';
import { GtmService } from '@core/services/analytics/gtm.service';
import { CustomizationUtilService } from '@core/services/customization-util/customization-util.service';
import { FreebieService } from '@core/services/freebie/freebie.service';
import { Menu } from '@shared/models/menu';
import { MenuModel } from '@shared/models/menu-model';

import { BaseComponent } from '@core/components/base/base.component';

import throttle from './throttle';
import { MenuProductFilterPipe } from '@shared/pipes/menu-product-filter.pipe';
import { CategoryFilterPipe } from '@shared/pipes/category-filter.pipe';
import { DeferImageLoadDirective } from '../../shared/directives/defer-image-load.directive';
import { PonchoUIModule } from '@poncho/ui';
import { NgClass, NgTemplateOutlet, NgStyle } from '@angular/common';
import { PassStripComponent } from '../../shared/components/pass-strip/pass-strip.component';
import { TruncateTextComponent } from '@app/shared/components/truncate-text/truncate-text.component';
import { FormsModule } from '@angular/forms';
import { FeatureToggleDirective } from '../../shared/directives/feature-toggle.directive';
import { MenuLoaderComponent } from '../../shared/components/dummy-template/menu-loader/menu-loader.component';
import { CatalogApiResponse, Membership, OutletState, OutletStatus } from '@app/shared/models/api-response/catalog-api-response';
import { OutletStatusStripService } from '@app/core/services/outlet-status-strip/outlet-status-strip.service';
import { OutletStatusStripComponent } from '@app/shared/components/outlet-status-strip/outlet-status-strip.component';
import { MenuOverlayRef } from '@app/core/services/menu_filter/menu-overlay-ref';

@Component({
    selector: 'app-menu',
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.scss'],
    standalone: true,
    imports: [
        MenuLoaderComponent,
        FeatureToggleDirective,
        FormsModule,
        NgClass,
        RouterLink,
        NgTemplateOutlet,
        NgStyle,
        PonchoUIModule,
        DeferImageLoadDirective,
        OutletStatusStripComponent,
        CategoryFilterPipe,
        MenuProductFilterPipe,
        TruncateTextComponent,
        PassStripComponent
    ]
})
export class MenuComponent extends BaseComponent {
    menu: Menu;
    cart_items: any;
    menuModel: MenuModel;
    categoryCount: { [key: number]: number } = {};
    redirectionInProgress = false;
    menuDataFetched = false;

    selectedCategory = {
        id: Number(this.storageService.get('category_id') ? Number(this.storageService.get('category_id')) : 0) as any
    };

    cartItemQuantity = 0;
    primaryBrandId = environment.brand.id;

    @ViewChildren('categoryWrap') categoryWrap: QueryList<ElementRef>;
    @ViewChild('categorySelectorById') categorySelectorById: ElementRef;
    @ViewChild('menuBtn') menuBtn: ElementRef;
    @ViewChild('mobileHeader') mobileHeader: ElementRef;

    destroySubject$: Subject<void> = new Subject();

    outlet: CatalogApiResponse['outlet'];
    isVeg = false;
    updateCategory: Function;

    samplingStripText = '';

    productPrices = {};
    passModel = {};
    queryParams: any;
    showFreebieTnC = false;
    expiredPassUser = true;
    customerPassDetails = {};
    cartItemPrice = 0;

    // Signals
    categoryWrapSignal = signal<ElementRef[]>([]); // Stores category DOM references
    menuModelSignal = signal<boolean>(false); // Indicates menu model readiness
    navigationScrollSignal = signal<boolean>(false); // Indicates scroll completion
    productQuantities = {};

    showFreebieStrip: boolean;
    showOutletStatusStrip: boolean;
    outletStatusStrip: OutletStatus | null;
    menuOverlayOpen = false;
    menuRef: MenuOverlayRef;

    constructor(
        private outletService: OutletService,
        private menuService: MenuService,
        private cartService: CartService,
        private filterService: FilterService,
        private storageService: StorageService,
        private priceService: PriceService,
        private passService: PassService,
        private outletStatusService: OutletStatusService,
        private changeDetectorRef: ChangeDetectorRef,
        private menuFilterService: MenuFilterService,
        private router: Router,
        private freebieService: FreebieService,
        private outletStatusStripService: OutletStatusStripService,
        private menuUtil: MenuUtilService,
        private route: ActivatedRoute,
        private customizationUtil: CustomizationUtilService,
        private gtmService: GtmService
    ) {
        super();
        this.updateCategory = throttle(this.updateCategoryOnScroll, 300);

        this.initializeRouterScrollListener();

        // Reactive effect to perform actions when all required conditions are met
        effect(() => {
            const menuReady = this.menuModelSignal();
            const scrollDone = this.navigationScrollSignal();
            const categoryRefs = this.categoryWrapSignal();

            if (menuReady && scrollDone && categoryRefs.length > 0) {
                const categoryId = Number(this.storageService.get('category_id'));
                this.selectCategory(categoryId);
            }
        });
    }

    private initializeRouterScrollListener(): void {
        this.router.events
            .pipe(
                filter((event): event is Scroll => event instanceof Scroll),
                takeUntil(this.destroySubject$)
            )
            .subscribe(() => {
                this.navigationScrollSignal.set(true);
            });
    }

    async ngOnInitBrowser() {
        let brand_id = this._getMenuBrandId();

        this.route.queryParams.pipe(takeUntil(this.destroySubject$)).subscribe(params => {
            this.queryParams = params;
            this.deepLinking(); //for menu screen banner click
        });

        const isCatalogPresent = await this.isCatalogAvailable(brand_id);
        brand_id = Number(this.queryParams?.brand_id || brand_id);

        if (!isCatalogPresent || !brand_id) {
            this.router.navigate(['/'], { replaceUrl: true });
            return;
        }

        await this.fetchOutletData(brand_id);

        this.outletService.state.pipe(takeUntil(this.destroySubject$)).subscribe(data => {
            if (data && data.outlet) {
                this.gtmService.logPageDetails(data, 'menu_page');
                this.outletStatusService.checkOutletStatus(data);
                this._handleOutletStatusStrip(data);
                this.outlet = data.outlet;
                this._checkActiveBottomStrips();
            }
        });

        this.menuService.menuModel.pipe(takeUntil(this.destroySubject$)).subscribe((data: MenuModel) => {
            this.menuModel = data;
            this.deepLinking();
            this.showFreebieTnC = this.freebieService.getExcludedProductList(data);
            this.menu = this.getFilteredProducts(this.filterService.isVeg$.value);

            if (
                this.menuModel &&
                this.menuModel.product_categories &&
                Object.values(this.menuModel.product_categories).length > 0
            ) {
                this.menuModelSignal.set(true);
            }
        });

        this.filterService.isVeg$.pipe(takeUntil(this.destroySubject$)).subscribe((is_veg: boolean) => {
            this.isVeg = is_veg;
            this.menu = this.getFilteredProducts(is_veg);
            this.categoryCount = this._getCategoryCount(this.menu);
            this.changeDetectorRef.markForCheck();
        });

        this.cartService.state.pipe(takeUntil(this.destroySubject$)).subscribe((cart: any) => {
            this.cart_items = Object.values(cart.items);
            this.cartItemQuantity = this.cart_items.reduce((acc: number, item: any) => acc + item.quantity, 0) as number;
            this.cartItemPrice = this.cart_items.reduce((acc, item) => acc + item.price * item.quantity, 0);
            this.productQuantities = this.cartService.getProductQuantities(this.cart_items);
            this.getSamplingStrip();
            this.changeDetectorRef.detectChanges();
        });

        this.passService.initCustomerPasses();

        this.priceService
            .getMenuPrices()
            .pipe(takeUntil(this.destroySubject$))
            .subscribe((prices: any) => {
                if (prices && prices.products) {
                    this.productPrices = prices.products;
                }
            });

        this.passService.passModel$.pipe(takeUntil(this.destroySubject$)).subscribe((data: any) => {
            this.passModel = data;
        });

        this.passService.customerPassDetails$.pipe(takeUntil(this.destroySubject$)).subscribe((data: any) => {
            this.customerPassDetails = data;
            this.expiredPassUser = Boolean(
                data?.eatclub_trial_expired &&
                    (data?.customer_pass_details?.days_left == 0 || !Object.keys(data?.customer_pass_details).length)
            );
        });

        this.freebieService.state.pipe(takeUntil(this.destroySubject$)).subscribe(() => {
            this.getSamplingStrip();
        });
    }

    private _handleOutletStatusStrip(data: CatalogApiResponse) {
        const { outlet_status_strip, outlet_state } = data.outlet;
        if (outlet_state !== OutletState.PREORDER) {
            this.outletStatusStrip = outlet_status_strip;
            this.outletStatusStripService.hydrateOutletStatusStrip({ ...outlet_status_strip, outlet_state });
        }
    }

    ngAfterViewInitBrowser() {
        const subscriber$ = this.categoryWrap.changes.subscribe(() => {
            this.categoryWrapSignal.set(this.categoryWrap.toArray());
            subscriber$.unsubscribe();
        });

        // initialize scroll listener after 500ms, since we don't need immediately
        setTimeout(() => {
            this.attachScrollListener();
        }, 500);
    }

    private attachScrollListener() {
        window.addEventListener('scroll', this.updateCategorySelection);
    }

    updateCategorySelection = () => {
        this.updateCategory();
    };

    deepLinking() {
        //deeplinking with category_id
        if (
            this.queryParams &&
            this.queryParams.category_id &&
            this.menuModel &&
            this.menuModel.product_categories &&
            Object.values(this.menuModel.product_categories).length > 0
        ) {
            if (Object.values(this.menuModel.product_categories).find(category => category.id == this.queryParams.category_id)) {
                this.selectCategory(this.queryParams.category_id);
            }
        }

        //deeplinking with product_id
        if (this.queryParams && this.queryParams.product_id && this.menuService.isServiceable(this.outlet)) {
            this.customizationUtil.openCustomizationModel(this.queryParams.product_id, this.menuModel);
        }
    }

    ngOnDestroyBrowser() {
        this.destroySubject$.next();
        this.destroySubject$.complete();
        this.filterService.toggleVeg();
        this.menuFilterService.closeOverlay();
        window.removeEventListener('scroll', this.updateCategorySelection);
    }

    async fetchOutletData(brand_id) {
        const outletData = await this.outletService.getOutletData(brand_id);
        if (outletData && outletData.outlet) {
            this.menuDataFetched = Boolean(outletData);
            this.changeDetectorRef.detectChanges();
        }
    }

    private _getCategoryCount(menu: Menu) {
        return menu.data.reduce((accumulator, product) => {
            menu.sequence.map(category => {
                const category_id = category.id;
                accumulator[category_id] = accumulator[category_id] || 0;
                accumulator[category.id] =
                    category.id === product.category_id ? accumulator[category_id] + 1 : accumulator[category_id];
                return accumulator;
            });
            return accumulator;
        }, {});
    }

    openFreebieTC() {
        this.freebieService.openFreebieTC(this.menuModel.products, this.menuModel.brand_id);
    }

    updateCategoryOnScroll() {
        const categoryWrappers = this.categoryWrap ? this.categoryWrap.toArray() : [];

        for (const categoryWrapper of categoryWrappers) {
            const thisElement = categoryWrapper.nativeElement;

            let activeCategoryFound = false;
            if (this.isOnScreen(thisElement)) {
                const idAttribute = thisElement.getAttribute('id');

                if (idAttribute) {
                    this.selectedCategory.id = idAttribute.split('category-')[1];

                    if (this.selectedCategory.id) {
                        this.storageService.set('category_id', this.selectedCategory.id);
                        activeCategoryFound = true;
                    }
                }
            }

            if (activeCategoryFound) {
                break;
            }
        }

        if (this.categorySelectorById) {
            this.scrollToActiveMenu();
        }
    }

    isOnScreen(elem) {
        if (!elem) return false;
        const bounding = elem.getBoundingClientRect();
        const h = window.innerHeight || document.documentElement.clientHeight;
        const w = window.innerWidth || document.documentElement.clientWidth;
        return bounding.bottom >= h / 4 && bounding.right >= 0 && bounding.top <= h && bounding.left <= w;
    }

    scrollToActiveMenu() {
        const container = this.categorySelectorById.nativeElement;

        for (let i = 0; i < container.childElementCount; i++) {
            const activeMenu = container.children[i].children[0];

            if (activeMenu?.id === `category-selector-${this.selectedCategory.id}` && !this.isOnScreen(activeMenu)) {
                const left = activeMenu.offsetLeft;
                const currScroll = container.getBoundingClientRect().left + document.body.scrollLeft;
                const contWidth = container.offsetWidth / 2;
                const activeOuterWidth = activeMenu.offsetWidth / 2;
                container.scrollLeft = left + currScroll - contWidth + activeOuterWidth;
            }
        }
    }

    getFilteredProducts(is_veg: boolean): Menu {
        if (!this.menuModel || !this.menuModel.menu) {
            return new Menu();
        }

        const categories = new Set();
        const newMenuData = this.menuModel.menu.data.filter(product => {
            const is_selected = is_veg ? product.veg : true;
            if (is_selected) {
                categories.add(product.category_id);
            }
            return is_selected;
        });

        const newCategories = [...categories];
        const newMenuOrder = this.menuModel.menu.sequence.filter(
            (category: any) => newCategories.includes(category.id) && category.saleable && !category.is_subscribable
        );

        if (newMenuOrder.length > 0) {
            const id = Number(this.storageService.get('category_id'));
            const item = newMenuOrder.filter(product => product.id === id);
            const new_category_id = item[0] ? item[0].id : newMenuOrder[0].id;
            this.categoryWrap.changes.subscribe(() => {
                this.selectCategory(new_category_id);
            });
        }

        return new Menu(newMenuData, newMenuOrder);
    }

    selectCategory(id) {
        if (!id) return false;
        this.storageService.set('category_id', id);
        this.selectedCategory.id = id;
        if (this.categoryWrap) {
            const categoryList = this.categoryWrap.toArray();
            for (let i = 0; i < categoryList.length; i++) {
                const el = categoryList[i].nativeElement;
                if (el.id === `category-${id}`) {
                    const mobileHeaderHeight = this.mobileHeader ? this.mobileHeader.nativeElement.offsetHeight : 0;
                    const elemTop = el.offsetTop - mobileHeaderHeight - (this.isMobile ? 0 : this.samplingStripText ? 100 : 70);
                    window.scroll(0, elemTop);
                }
            }

            if (this.categorySelectorById) {
                this.scrollToActiveMenu();
            }
        }
    }

    isAddButtonActive(productId: number) {
        return this.menuService.isAddButtonActive(productId);
    }

    getButtonLabel(productId: number) {
        return this.menuService.getButtonLabel(productId);
    }

    updateCart(operation, productId) {
        this.menuUtil.updateCart(operation, productId, this.menuModel);
    }

    handleDisabledState(product) {
        this.menuUtil.handleDisabledState(product, this.outlet);
    }

    // Pass related Code

    openMenu() {
        if (this.menuOverlayOpen) {
            this.menuRef.close();
            this.menuOverlayOpen = false;
            return;
        }

        this.menuOverlayOpen = true;
        const productCategories = JSON.parse(JSON.stringify(this.menu.sequence));
        const categoryCount = this._getCategoryCount(this.menu);

        this.menuRef = this.menuFilterService.open({
            elemRef: this.menuBtn,
            data: {
                categories: productCategories,
                categoryCount: categoryCount,
                selectCategoryId: this.selectedCategory.id
            }
        });

        this.menuRef.backdropClick().subscribe(() => this.menuRef.close());

        this.menuRef.afterClosed().subscribe(() => {
            this.menuOverlayOpen = false;
        });
    }

    toggleOnlyVeg() {
        this.filterService.toggleVeg(!this.isVeg);
    }

    isLoggedIn() {
        return this.storageService.get('session-id') && this.storageService.get('phone_no');
    }

    proceedToCart() {
        this.redirectionInProgress = true;
        this.submitCart();
    }

    submitCart() {
        this.cartService.submitCart().finally(() => {
            this.redirectionInProgress = false;
        });
    }

    private _getMenuBrandId() {
        const cookie_brand_id = this.storageService.get('brand_id');

        if (cookie_brand_id) {
            return cookie_brand_id;
        }
        return environment.brand.id === 19 ? null : environment.brand.id;
    }

    async isCatalogAvailable(brand_id): Promise<boolean> {
        if (this.queryParams?.brand_id) {
            brand_id = Number(this.queryParams.brand_id);
            const outletData: CatalogApiResponse = await this.outletService.getOutletDataWithoutStateUpdate(environment.brand.id);
            const memberships = outletData.outlet.memberships;

            const isOutletSelected = this.outletService.isOutletSelected();
            const isValidMembership =
                memberships && Object.values(memberships).some((member: Membership) => member.id === brand_id);

            if (!isOutletSelected) {
                this.gtmService.logWebAnalyticsEvent('menu_page', 'Location not selected');
                return false;
            }

            if (isValidMembership) {
                this.storageService.set('brand_id', brand_id);
                return true;
            }

            return false;
        }

        return true;
    }

    getSamplingStrip() {
        if (this.outlet) {
            this.samplingStripText = this.freebieService.getSamplingStrip(this.outlet.brand_id);
        }
        this._checkActiveBottomStrips();
    }

    _checkActiveBottomStrips() {
        if (this.outletStatusStrip) {
            this.showOutletStatusStrip = true;
            this.showFreebieStrip = false;
        } else if (this.samplingStripText) {
            this.showFreebieStrip = true;
        }
    }
}
